import React from 'react';
import Editor, { DiffEditor, EditorProps, DiffEditorProps } from '@monaco-editor/react';
import { useAppTheme } from '@ui-library/tokens/themeContext';
import { EditorContainer } from './styles';
import { CopyToClipboard } from '../icons/copyToClipboard';

export type MonacoLanguageType = 'javascript' | 'python' | 'json' | 'text' | 'yaml' | 'sql';

interface IMonacoEditorProps extends Omit<EditorProps, 'onChange'> {
  code: string;
  height?: string;
  onChange?: (code: string) => void;
  readOnly?: boolean;
  enableCopy?: boolean;
  hideLineNumber?: boolean;
  noBorder?: boolean;
}

interface IMonacoDiffEditorProps extends DiffEditorProps {
  height?: string;
  original: string;
  modified: string;
  language: MonacoLanguageType;
  noBorder?: boolean;
  compact?: boolean;
}

export const MonacoEditor = ({
  code,
  language,
  onChange,
  readOnly,
  enableCopy,
  hideLineNumber,
  className,
  options = {},
  noBorder,
  ...restProps
}: IMonacoEditorProps) => {
  const { resolvedMode } = useAppTheme();
  const onCodeChange = (code: string | undefined) => {
    if (onChange) {
      onChange(code || '');
    }
  };
  const height = Object.prototype.hasOwnProperty.call(restProps, 'height')
    ? restProps.height
    : '500px';

  const editorTheme = restProps.theme ?? (resolvedMode === 'dark' ? 'vs-dark' : 'light');

  return (
    <EditorContainer $noBorder={noBorder} className={className}>
      {enableCopy && (
        <CopyToClipboard className="copy_button" content={code} showIcon={false}>
          Copy
        </CopyToClipboard>
      )}
      <Editor
        width="100%"
        theme={editorTheme}
        language={language}
        value={code}
        onChange={onCodeChange}
        options={{
          lineNumbers: hideLineNumber ? 'off' : undefined,
          minimap: {
            enabled: false,
          },
          lightbulb: {
            enabled: false,
          },
          readOnly,
          fontSize: 13,
          scrollBeyondLastLine: false,
          overviewRulerLanes: 0,
          scrollbar: { alwaysConsumeMouseWheel: false },
          lineHeight: 1.4,
          ...options,
        }}
        {...restProps}
        height={height}
      />
    </EditorContainer>
  );
};

export const MonacoDiffEditor = ({
  height = '500px',
  className,
  original,
  modified,
  language,
  noBorder,
  compact,
  options = {},
  ...restProps
}: IMonacoDiffEditorProps) => {
  const { resolvedMode } = useAppTheme();
  const editorTheme = restProps.theme ?? (resolvedMode === 'dark' ? 'vs-dark' : 'light');

  return (
    <EditorContainer $noBorder={noBorder} className={className}>
      <DiffEditor
        width="100%"
        height={height}
        theme={editorTheme}
        language={language}
        original={original}
        modified={modified}
        options={{
          renderSideBySide: !compact,
          minimap: {
            enabled: false,
          },
          readOnly: true,
          fontSize: 13,
          wordWrap: 'on',
          scrollBeyondLastLine: false,
          overviewRulerLanes: 0,
          scrollbar: { alwaysConsumeMouseWheel: false },
          lineHeight: 1.4,
          ...options,
        }}
        {...restProps}
      />
    </EditorContainer>
  );
};
